/**
 * Admission Automation Engine - Main Dashboard
 * Schools can manage admissions, view leads, track seats
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import WelcomeVideoGenerator from '../components/admission/WelcomeVideoGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon, color = 'blue', trend }) => (
  <div className={`bg-white rounded-xl p-5 shadow-sm border-l-4 border-${color}-500`}>
    <div className="flex justify-between items-start">
      <div>
        <p className="text-gray-500 text-sm">{title}</p>
        <h3 className="text-2xl font-bold mt-1">{value}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        {trend && (
          <span className={`text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week
          </span>
        )}
      </div>
      <span className="text-3xl">{icon}</span>
    </div>
  </div>
);

// Live Seat Counter Component
const LiveSeatCounter = ({ classData, schoolId }) => {
  const fillPercentage = ((classData.total_seats - classData.available_seats) / classData.total_seats) * 100;
  
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold">{classData.class_name}</h4>
        <span className={`px-2 py-1 rounded-full text-xs ${
          classData.available_seats === 0 ? 'bg-red-100 text-red-600' :
          classData.available_seats <= 5 ? 'bg-orange-100 text-orange-600' :
          'bg-green-100 text-green-600'
        }`}>
          {classData.available_seats === 0 ? 'FULL' : `${classData.available_seats} seats left`}
        </span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
        <div 
          className={`h-3 rounded-full transition-all duration-500 ${
            fillPercentage >= 90 ? 'bg-red-500' :
            fillPercentage >= 70 ? 'bg-orange-500' :
            'bg-green-500'
          }`}
          style={{ width: `${fillPercentage}%` }}
        />
      </div>
      
      <div className="flex justify-between text-sm text-gray-500">
        <span>₹{classData.annual_fee?.toLocaleString()}/year</span>
        <span>{Math.round(fillPercentage)}% filled</span>
      </div>
    </div>
  );
};

// Lead Card Component
const LeadCard = ({ lead, onStatusUpdate }) => {
  const statusColors = {
    new: 'bg-blue-100 text-blue-600',
    contacted: 'bg-yellow-100 text-yellow-600',
    visited: 'bg-purple-100 text-purple-600',
    admitted: 'bg-green-100 text-green-600',
    lost: 'bg-red-100 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h4 className="font-semibold">{lead.parent_name || 'Parent'}</h4>
          <p className="text-sm text-gray-500">{lead.parent_phone}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[lead.status] || statusColors.new}`}>
          {lead.status?.toUpperCase()}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Looking for: <strong>{lead.class_needed}</strong>
      </p>
      <div className="flex gap-2">
        <a 
          href={`https://wa.me/91${lead.parent_phone}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
        >
          💬 WhatsApp
        </a>
        <button 
          onClick={() => onStatusUpdate(lead, 'contacted')}
          className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
        >
          📞 Called
        </button>
      </div>
    </div>
  );
};

// Admission Setup Modal
const AdmissionSetupModal = ({ isOpen, onClose, schoolId, token, onSave }) => {
  const [classes, setClasses] = useState([
    { class_name: 'Class 1', total_seats: 40, available_seats: 40, annual_fee: 35000, admission_fee: 5000, includes: ['Books', 'Uniform'] }
  ]);
  const [discounts, setDiscounts] = useState([
    { name: 'Early Bird', discount_type: 'fixed', discount_value: 5000, valid_till: '2025-03-31' }
  ]);
  const [saving, setSaving] = useState(false);

  const addClass = () => {
    const lastClass = classes[classes.length - 1];
    const nextNum = parseInt(lastClass?.class_name?.replace('Class ', '') || 0) + 1;
    setClasses([...classes, { 
      class_name: `Class ${nextNum}`, 
      total_seats: 40, 
      available_seats: 40, 
      annual_fee: 35000,
      admission_fee: 5000,
      includes: ['Books', 'Uniform']
    }]);
  };

  const updateClass = (index, field, value) => {
    const updated = [...classes];
    updated[index][field] = field.includes('fee') || field.includes('seats') ? Number(value) : value;
    setClasses(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admission/profile/setup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId,
          academic_year: '2025-26',
          admission_open: true,
          classes,
          discount_rules: discounts,
          max_discount_percentage: 20
        })
      });
      const data = await res.json();
      if (data.success) {
        onSave();
        onClose();
      }
    } catch (err) {
      console.error('Error saving:', err);
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">🎓 Admission Setup</h2>
          <p className="text-gray-500 text-sm">Class-wise fees और seats configure करें</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Classes */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Classes & Fees</h3>
              <button onClick={addClass} className="text-sm text-blue-500 hover:underline">+ Add Class</button>
            </div>
            
            <div className="space-y-3">
              {classes.map((cls, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    value={cls.class_name}
                    onChange={(e) => updateClass(idx, 'class_name', e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Class Name"
                  />
                  <input
                    type="number"
                    value={cls.total_seats}
                    onChange={(e) => updateClass(idx, 'total_seats', e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Total Seats"
                  />
                  <input
                    type="number"
                    value={cls.available_seats}
                    onChange={(e) => updateClass(idx, 'available_seats', e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Available"
                  />
                  <input
                    type="number"
                    value={cls.annual_fee}
                    onChange={(e) => updateClass(idx, 'annual_fee', e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Annual Fee"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Discounts */}
          <div>
            <h3 className="font-semibold mb-3">Discount Rules</h3>
            <div className="space-y-3">
              {discounts.map((disc, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    value={disc.name}
                    onChange={(e) => {
                      const updated = [...discounts];
                      updated[idx].name = e.target.value;
                      setDiscounts(updated);
                    }}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Discount Name"
                  />
                  <input
                    type="number"
                    value={disc.discount_value}
                    onChange={(e) => {
                      const updated = [...discounts];
                      updated[idx].discount_value = Number(e.target.value);
                      setDiscounts(updated);
                    }}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="Amount (₹)"
                  />
                  <input
                    type="date"
                    value={disc.valid_till}
                    onChange={(e) => {
                      const updated = [...discounts];
                      updated[idx].valid_till = e.target.value;
                      setDiscounts(updated);
                    }}
                    className="px-3 py-2 border rounded-lg"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Setup'}
          </button>
        </div>
      </div>
    </div>
  );
};

// AI Exam Tab Component for Admission Engine
function AIExamTab({ token, schoolId, language }) {
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [section, setSection] = useState('exams'); // exams, results, analytics
  const [form, setForm] = useState({
    class_name: 'Class 1', difficulty: 'medium',
    subjects: ['language', 'math', 'logic', 'gk', 'creativity'],
    questions_per_subject: 3, time_limit_minutes: 15, cutoff_percentage: 50,
  });

  useEffect(() => { loadAll(); }, [schoolId]);

  const loadAll = async () => {
    if (!schoolId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [exRes, resRes, anRes] = await Promise.all([
        fetch(`${API_URL}/api/admission/exam/list/${schoolId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/admission/exam/results/${schoolId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/admission/exam/analytics/${schoolId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      if (exRes.success) setExams(exRes.exams || []);
      if (resRes.success) setResults(resRes.results || []);
      if (anRes.success) setAnalytics(anRes);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const createExam = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admission/exam/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ school_id: schoolId, ...form }),
      });
      const data = await res.json();
      if (data.success) {
        setShowCreate(false);
        loadAll();
      }
    } catch (err) { console.error(err); }
    setCreating(false);
  };

  const copyLink = (tk) => {
    const url = `${window.location.origin}/admission-exam/${tk}`;
    navigator.clipboard.writeText(url);
  };

  const subjectOptions = [
    { key: 'language', label: 'भाषा' }, { key: 'math', label: 'गणित' },
    { key: 'logic', label: 'तर्क' }, { key: 'gk', label: 'सामान्य ज्ञान' },
    { key: 'creativity', label: 'रचनात्मकता' },
  ];

  const classOptions = ['Nursery', 'LKG', 'UKG', ...Array.from({length: 12}, (_, i) => `Class ${i+1}`)];

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-4" data-testid="ai-exam-tab">
      {/* Section Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[
            { id: 'exams', label: language === 'hi' ? 'Exams' : 'Exams', icon: '📝' },
            { id: 'results', label: language === 'hi' ? 'Results' : 'Results', icon: '📊' },
            { id: 'analytics', label: language === 'hi' ? 'Analytics' : 'Analytics', icon: '📈' },
          ].map(s => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${section === s.id ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
              {s.icon} {s.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="create-exam-btn">
          + {language === 'hi' ? 'नया Exam बनाएं' : 'Create Exam'}
        </button>
      </div>

      {/* Exams Section */}
      {section === 'exams' && (
        <div className="space-y-3">
          {exams.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-5xl block mb-3">🎮</span>
              <p className="font-semibold text-gray-700">{language === 'hi' ? 'कोई Exam नहीं बना' : 'No exams created yet'}</p>
              <p className="text-sm text-gray-400 mt-1">{language === 'hi' ? '"नया Exam बनाएं" button click करें' : 'Click "Create Exam" to get started'}</p>
            </div>
          ) : exams.map(ex => (
            <div key={ex.exam_id} className="bg-white rounded-xl p-4 border shadow-sm flex items-center justify-between" data-testid={`exam-card-${ex.exam_id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-800">{ex.class_name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${ex.difficulty === 'easy' ? 'bg-green-100 text-green-600' : ex.difficulty === 'hard' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                    {ex.difficulty === 'easy' ? 'आसान' : ex.difficulty === 'hard' ? 'कठिन' : 'मध्यम'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${ex.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                    {ex.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400">{ex.total_questions} questions | {ex.time_limit_minutes} min | Cutoff: {ex.cutoff_percentage}% | Attempts: {ex.attempt_count || 0}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => copyLink(ex.token)} className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600" data-testid="copy-link-btn">
                  📋 Copy Link
                </button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`${ex.class_name} Admission Exam: ${window.location.origin}/admission-exam/${ex.token}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700">
                  💬 WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Section */}
      {section === 'results' && (
        <div className="space-y-3">
          {results.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-5xl block mb-3">📊</span>
              <p className="font-semibold text-gray-700">{language === 'hi' ? 'अभी कोई result नहीं' : 'No results yet'}</p>
            </div>
          ) : results.map(r => (
            <div key={r.attempt_id} className="bg-white rounded-xl p-4 border shadow-sm" data-testid={`result-card-${r.attempt_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-gray-800">{r.student_name}</h4>
                  <p className="text-xs text-gray-400">{r.class_name} | {r.parent_phone} | {new Date(r.submitted_at).toLocaleDateString('hi-IN')}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black" style={{color: r.passed ? '#16a34a' : '#dc2626'}}>
                    {r.percentage}%
                  </div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => <span key={s} className={`text-sm ${s <= r.stars ? '' : 'opacity-20'}`}>⭐</span>)}
                  </div>
                </div>
              </div>
              {/* Subject bars */}
              {r.subject_scores && (
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {Object.entries(r.subject_scores).map(([subj, sc]) => {
                    const pct = sc.total > 0 ? Math.round((sc.scored / sc.total) * 100) : 0;
                    return (
                      <div key={subj} className="text-center">
                        <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                          <div className={`h-2 rounded-full ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{width: `${pct}%`}} />
                        </div>
                        <p className="text-[10px] text-gray-400">{subj}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {r.ai_recommendation && (
                <div className={`p-3 rounded-lg text-sm ${r.passed ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <p className="font-medium mb-1">AI Recommendation:</p>
                  <p className="text-xs">{r.ai_recommendation}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Analytics Section */}
      {section === 'analytics' && analytics && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-xs text-gray-500 uppercase">Total Attempts</p>
              <p className="text-3xl font-bold text-gray-800">{analytics.total_attempts}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-xs text-gray-500 uppercase">Avg Score</p>
              <p className="text-3xl font-bold text-blue-600">{analytics.avg_score}%</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-xs text-gray-500 uppercase">Pass Rate</p>
              <p className="text-3xl font-bold text-green-600">{analytics.pass_rate}%</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-xs text-gray-500 uppercase">Classes</p>
              <p className="text-3xl font-bold text-purple-600">{Object.keys(analytics.class_breakdown || {}).length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create Exam Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-exam-modal">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold">🎮 {language === 'hi' ? 'नया Entrance Exam बनाएं' : 'Create Entrance Exam'}</h2>
                <button onClick={() => setShowCreate(false)} className="text-2xl text-gray-400 hover:text-gray-600">&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                <select value={form.class_name} onChange={e => setForm({...form, class_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg" data-testid="exam-class-select">
                  {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty / कठिनाई</label>
                <div className="flex gap-2">
                  {[{v:'easy',l:'आसान',c:'bg-green-100 text-green-700 border-green-300'},{v:'medium',l:'मध्यम',c:'bg-yellow-100 text-yellow-700 border-yellow-300'},{v:'hard',l:'कठिन',c:'bg-red-100 text-red-700 border-red-300'}].map(d => (
                    <button key={d.v} onClick={() => setForm({...form, difficulty: d.v})}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition ${form.difficulty === d.v ? d.c : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subjects</label>
                <div className="flex flex-wrap gap-2">
                  {subjectOptions.map(s => (
                    <button key={s.key}
                      onClick={() => setForm({...form, subjects: form.subjects.includes(s.key) ? form.subjects.filter(x => x !== s.key) : [...form.subjects, s.key]})}
                      className={`px-3 py-1.5 rounded-lg text-sm border ${form.subjects.includes(s.key) ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Questions/Subject</label>
                  <select value={form.questions_per_subject} onChange={e => setForm({...form, questions_per_subject: Number(e.target.value)})} className="w-full px-3 py-2 border rounded-lg">
                    {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Time (min)</label>
                  <select value={form.time_limit_minutes} onChange={e => setForm({...form, time_limit_minutes: Number(e.target.value)})} className="w-full px-3 py-2 border rounded-lg">
                    {[5,10,15,20,30,45,60].map(n => <option key={n} value={n}>{n} min</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cutoff %</label>
                  <select value={form.cutoff_percentage} onChange={e => setForm({...form, cutoff_percentage: Number(e.target.value)})} className="w-full px-3 py-2 border rounded-lg">
                    {[30,40,50,60,70,80].map(n => <option key={n} value={n}>{n}%</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 text-sm text-orange-700">
                <p className="font-medium">Preview:</p>
                <p>{form.class_name} | {form.difficulty === 'easy' ? 'आसान' : form.difficulty === 'hard' ? 'कठिन' : 'मध्यम'} | {form.subjects.length * form.questions_per_subject} questions | {form.time_limit_minutes} min</p>
              </div>
            </div>
            <div className="p-6 border-t flex gap-3">
              <button onClick={() => setShowCreate(false)} className="flex-1 py-2 border rounded-lg text-gray-600">Cancel</button>
              <button onClick={createExam} disabled={creating || form.subjects.length === 0}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="submit-exam-btn">
                {creating ? 'AI Questions बना रहा है...' : '🎮 Exam बनाएं'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Dashboard Component
export default function AdmissionEngineDashboard({ token, schoolId }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Use first school if schoolId not provided
  const [currentSchoolId, setCurrentSchoolId] = useState(schoolId);

  useEffect(() => {
    loadData();
  }, [currentSchoolId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get admission profile
      const profileRes = await fetch(`${API_URL}/api/admission/profile/${currentSchoolId || 'default'}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profileData = await profileRes.json();
      setProfile(profileData.profile);

      // Get leads
      if (currentSchoolId) {
        const leadsRes = await fetch(`${API_URL}/api/school-match/interests/${currentSchoolId}?limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const leadsData = await leadsRes.json();
        setLeads(leadsData.interests || []);
      }

      // Get stats
      const statsRes = await fetch(`${API_URL}/api/admission/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData.stats);

    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const handleStatusUpdate = async (lead, newStatus) => {
    try {
      await fetch(`${API_URL}/api/school-match/interests/${currentSchoolId}/${lead.parent_phone}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const journeySteps = [
    {
      id: 'school-match',
      step: '01',
      title: language === 'hi' ? 'School Match' : 'School Match',
      description: language === 'hi' ? 'Parents को सही स्कूल listing, fees, seats और lead capture.' : 'Listing, fees, seats, and lead capture for parents.',
      actionLabel: language === 'hi' ? 'Public flow देखें' : 'View public flow',
      href: '/admission',
      public: true,
    },
    {
      id: 'live-seats',
      step: '02',
      title: language === 'hi' ? 'Live Seat Counter' : 'Live Seat Counter',
      description: language === 'hi' ? 'Real-time seats दिखाकर urgency create करें।' : 'Create urgency with real-time seat visibility.',
      actionLabel: language === 'hi' ? 'Seats manage करें' : 'Manage seats',
      tab: 'seats',
    },
    {
      id: 'nearby-whatsapp',
      step: '03',
      title: language === 'hi' ? 'Nearby Parents WhatsApp' : 'Nearby Parents WhatsApp',
      description: language === 'hi' ? 'Geo-fence से nearby parents तक WhatsApp reach.' : 'Use geo-fencing to reach nearby parents.',
      actionLabel: language === 'hi' ? 'Geo flow खोलें' : 'Open geo flow',
      tab: 'geo',
    },
    {
      id: 'ai-calling',
      step: '04',
      title: language === 'hi' ? 'AI Calling Bot' : 'AI Calling Bot',
      description: language === 'hi' ? 'Lead follow-up, scripted calling और response workflow.' : 'Lead follow-up, scripted calling, and response workflow.',
      actionLabel: language === 'hi' ? 'Leads देखें' : 'View leads',
      tab: 'leads',
    },
    {
      id: 'ai-exam',
      step: '05',
      title: language === 'hi' ? 'AI Entrance Test' : 'AI Entrance Test',
      description: language === 'hi' ? 'Entrance exam create, share और evaluate करें।' : 'Create, share, and evaluate entrance exams.',
      actionLabel: language === 'hi' ? 'Exam setup करें' : 'Set up exam',
      tab: 'exam',
    },
    {
      id: 'fee-negotiator',
      step: '06',
      title: language === 'hi' ? 'AI Fee Negotiator' : 'AI Fee Negotiator',
      description: language === 'hi' ? 'Fee discussion और conversion को structured करें।' : 'Structure fee conversations and conversion.',
      actionLabel: language === 'hi' ? 'Fee flow खोलें' : 'Open fee flow',
      href: '/fees',
    },
    {
      id: 'padhai-emi',
      step: '07',
      title: language === 'hi' ? 'Padhai EMI' : 'Padhai EMI',
      description: language === 'hi' ? 'Parents के लिए EMI और school के लिए cash-flow clarity.' : 'EMI for parents with cash-flow clarity for school.',
      actionLabel: language === 'hi' ? 'EMI page खोलें' : 'Open EMI page',
      href: '/padhai-emi',
    },
    {
      id: 'welcome-video',
      step: '08',
      title: language === 'hi' ? 'Principal Welcome Video' : 'Principal Welcome Video',
      description: language === 'hi' ? 'Personalized welcome video बनाकर wow-factor दें।' : 'Create a personalized welcome video for wow factor.',
      actionLabel: language === 'hi' ? 'Video बनाएँ' : 'Create video',
      tab: 'welcome',
    },
  ];

  const handleJourneyAction = (step) => {
    if (step.tab) {
      setActiveTab(step.tab);
      return;
    }

    if (step.href) {
      // Public marketing page -> open in a NEW TAB so the admin stays logged in
      // in their workspace (no "logout" / re-login confusion on Back).
      if (step.public) {
        window.open(step.href, '_blank', 'noopener');
      } else {
        // Internal app route -> SPA navigation (fast, no full reload).
        navigate(step.href);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admission-engine-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            🎓 {language === 'hi' ? 'Admission Engine' : 'Admission Engine'}
          </h1>
          <p className="text-gray-500">
            {language === 'hi' ? 'अब वही 8-step journey जो public /admission page पर दिखती है' : 'Now aligned to the same 8-step journey shown on public /admission'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <a
            href="/admission"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 flex items-center gap-2"
            data-testid="open-public-admission-page-button"
          >
            🌐 {language === 'hi' ? 'Public /admission देखें' : 'Open public /admission'}
          </a>
          <button
            onClick={() => setShowSetup(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
            data-testid="open-admission-setup-button"
          >
            ⚙️ {language === 'hi' ? 'Setup' : 'Setup'}
          </button>
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-600 p-6 text-white shadow-lg" data-testid="admission-journey-hero">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
          {language === 'hi' ? 'Unified Message' : 'Unified Message'}
        </p>
        <h2 className="mt-2 text-3xl font-black">
          {language === 'hi' ? 'School Admin flow अब public Admission page के same 8 steps के साथ aligned है' : 'School Admin flow is now aligned with the same public 8-step admission story'}
        </h2>
        <p className="mt-3 max-w-4xl text-sm text-white/85" data-testid="admission-journey-hero-copy">
          {language === 'hi'
            ? 'Marketing page और admin workspace में अब एक ही message रहेगा: lead capture → live seats → nearby WhatsApp → AI calling → entrance exam → fee negotiation → EMI → welcome video.'
            : 'The marketing page and admin workspace now share one message: lead capture → live seats → nearby WhatsApp → AI calling → entrance exam → fee negotiation → EMI → welcome video.'}
        </p>
      </div>

      <div className="space-y-4" data-testid="admission-journey-steps-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-black text-slate-900">{language === 'hi' ? '8-Step Admission Journey' : '8-Step Admission Journey'}</h2>
            <p className="text-sm text-slate-500">{language === 'hi' ? 'यही वही sequence है जो /admission पर दिखता है — अब admin के लिए action-ready' : 'This is the same sequence shown on /admission — now action-ready for admins.'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {journeySteps.map((step) => (
            <div key={step.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`admission-journey-step-${step.step}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black tracking-[0.25em] text-orange-700">{step.step}</span>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{language === 'hi' ? 'Step' : 'Step'}</span>
              </div>
              <h3 className="mt-4 text-lg font-black text-slate-900">{step.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{step.description}</p>
              <button
                onClick={() => handleJourneyAction(step)}
                className="mt-5 w-full rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                data-testid={`admission-journey-action-${step.id}`}
              >
                {step.actionLabel}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">{language === 'hi' ? 'Operations Workspace' : 'Operations Workspace'}</h2>
        <p className="text-sm text-slate-500 mb-4">{language === 'hi' ? 'नीचे वही tools हैं जिनसे ऊपर वाले 8 steps को operate किया जाएगा।' : 'Use the tools below to operate the 8-step admission journey.'}</p>
      </div>
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: 'overview', label: language === 'hi' ? 'Overview' : 'Overview', icon: '📊' },
          { id: 'seats', label: language === 'hi' ? 'Live Seats' : 'Live Seats', icon: '🪑' },
          { id: 'leads', label: language === 'hi' ? 'Leads' : 'Leads', icon: '🎯' },
          { id: 'exam', label: language === 'hi' ? 'AI Exam' : 'AI Exam', icon: '🎮' },
          { id: 'welcome', label: language === 'hi' ? 'Welcome Video' : 'Welcome Video', icon: '🎬' },
          { id: 'geo', label: language === 'hi' ? 'Geo-Fence' : 'Geo-Fence', icon: '📍' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'text-orange-500 border-b-2 border-orange-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title={language === 'hi' ? 'Total Seats' : 'Total Seats'}
              value={stats?.total_seats || 0}
              icon="🪑"
              color="blue"
            />
            <StatCard 
              title={language === 'hi' ? 'Available' : 'Available'}
              value={stats?.available_seats || 0}
              subtitle={`${stats?.fill_rate || 0}% filled`}
              icon="✅"
              color="green"
            />
            <StatCard 
              title={language === 'hi' ? 'New Leads' : 'New Leads'}
              value={leads.filter(l => l.status === 'new').length}
              icon="🎯"
              color="orange"
            />
            <StatCard 
              title={language === 'hi' ? 'Converted' : 'Converted'}
              value={leads.filter(l => l.status === 'admitted').length}
              icon="🎉"
              color="purple"
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:shadow-lg transition-shadow">
              <span className="text-2xl">📱</span>
              <h3 className="font-semibold mt-2">Bulk WhatsApp</h3>
              <p className="text-sm opacity-80">Send to all leads</p>
            </button>
            <button className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:shadow-lg transition-shadow">
              <span className="text-2xl">📊</span>
              <h3 className="font-semibold mt-2">Export Report</h3>
              <p className="text-sm opacity-80">Download admission data</p>
            </button>
            <button 
              onClick={() => setShowSetup(true)}
              className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:shadow-lg transition-shadow"
            >
              <span className="text-2xl">⚙️</span>
              <h3 className="font-semibold mt-2">Configure</h3>
              <p className="text-sm opacity-80">Fees & Discounts</p>
            </button>
          </div>
        </div>
      )}

      {/* Live Seats Tab */}
      {activeTab === 'seats' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">🪑 Live Seat Availability</h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Live
            </div>
          </div>
          
          {profile?.classes?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.classes.map((cls, idx) => (
                <LiveSeatCounter key={idx} classData={cls} schoolId={currentSchoolId} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-4xl">🪑</span>
              <h3 className="font-semibold mt-4">No Classes Configured</h3>
              <p className="text-gray-500 mt-2">Setup admission profile to add classes</p>
              <button 
                onClick={() => setShowSetup(true)}
                className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                Setup Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">🎯 Parent Leads</h2>
            <div className="flex gap-2">
              {['all', 'new', 'contacted', 'visited'].map(status => (
                <button 
                  key={status}
                  className="px-3 py-1 text-sm rounded-full bg-gray-100 hover:bg-gray-200"
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {leads.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leads.map((lead, idx) => (
                <LeadCard key={idx} lead={lead} onStatusUpdate={handleStatusUpdate} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-4xl">🎯</span>
              <h3 className="font-semibold mt-4">No Leads Yet</h3>
              <p className="text-gray-500 mt-2">Leads will appear when parents show interest</p>
            </div>
          )}
        </div>
      )}

      {/* Welcome Video Tab */}
      {activeTab === 'welcome' && (
        <WelcomeVideoGenerator 
          token={token} 
          schoolId={currentSchoolId}
          schoolName={profile?.school_name}
        />
      )}

      {/* Geo-Fence Tab */}
      {activeTab === 'geo' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl p-6">
            <h2 className="text-xl font-bold">📍 Geo-Fencing</h2>
            <p className="opacity-80 mt-2">Auto WhatsApp when parents are nearby</p>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/20 rounded-lg p-4">
                <h4 className="font-semibold">Radius</h4>
                <p className="text-2xl font-bold">3 km</p>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <h4 className="font-semibold">Triggers Today</h4>
                <p className="text-2xl font-bold">0</p>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <h4 className="font-semibold">Status</h4>
                <p className="text-2xl font-bold">⏸️ Pending</p>
                <p className="text-xs opacity-70">WhatsApp approval needed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-800">
              ⚠️ Geo-Fencing will be activated after Meta WhatsApp approval (Expected: Tomorrow)
            </p>
          </div>
        </div>
      )}

      {/* AI Exam Tab */}
      {activeTab === 'exam' && (
        <AIExamTab token={token} schoolId={currentSchoolId} language={language} />
      )}

      {/* Setup Modal */}
      <AdmissionSetupModal 
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        schoolId={currentSchoolId}
        token={token}
        onSave={loadData}
      />
    </div>
  );
}
